/**
 * Roll builders for D&D 5e.
 * Each function returns a RollRequest payload ready for the roll:request WS command.
 * Pure functions, no side effects.
 *
 * T002 NOTE: All builders accept `RulesCharacterData` — the unified structural
 * supertype defined in ./rules/index.ts. The frontend `HudCharacterData`
 * (src/lib/character-hud-store.ts) is directly assignable to this type
 * without any `as unknown as` casts, by design. When extending these
 * signatures, prefer widening (more permissive parameter shape) over
 * narrowing so the structural contract holds.
 */

import type { RollRequest } from '../../domain.js';
import { dnd5eRules } from './rules/index.js';
import type { RulesCharacterData, AbilityStat, SkillId } from './rules/index.js';

// ─── Labels (Russian, copied from character-hud-store — shared/ can't import src/) ──

const STAT_LABELS: Record<AbilityStat, string> = {
  str: 'Сила', dex: 'Ловкость', con: 'Выносливость',
  int: 'Интеллект', wis: 'Мудрость', cha: 'Харизма',
};

const SKILL_LABELS: Record<SkillId, string> = {
  acrobatics: 'Акробатика', animalHandling: 'Уход за животными', arcana: 'Магия',
  athletics: 'Атлетика', deception: 'Обман', history: 'История',
  insight: 'Проницательность', intimidation: 'Запугивание', investigation: 'Анализ',
  medicine: 'Медицина', nature: 'Природа', perception: 'Внимательность',
  performance: 'Выступление', persuasion: 'Убеждение', religion: 'Религия',
  sleightOfHand: 'Ловкость рук', stealth: 'Скрытность', survival: 'Выживание',
};

// ─── Helpers ─────────────────────────────────────────────────────

function modExpr(mod: number): string {
  if (mod === 0) return '1d20';
  return mod > 0 ? `1d20+${mod}` : `1d20${mod}`;
}

// ─── Roll Builders ───────────────────────────────────────────────

/** Attack data shape from HudCharacterData.combat.attacks */
interface RollAttack {
  id: string;
  name: string;
  attackBonus: number;
  damage: string;
  damageType: string;
  meta?: string;
}

export function rollAbilityCheck(
  sheetId: string,
  stat: AbilityStat,
  data: RulesCharacterData,
): RollRequest {
  const mod = dnd5eRules.abilityMod(data.stats[stat]);
  return {
    sheetId,
    rollName: `Проверка ${STAT_LABELS[stat]}`,
    expression: modExpr(mod),
    fieldValues: { modifier: mod },
    isWhisper: false,
    isDualD20: false,
  };
}

export function rollSave(
  sheetId: string,
  stat: AbilityStat,
  data: RulesCharacterData,
): RollRequest {
  const mod = dnd5eRules.activeSaveMod(data, stat);
  return {
    sheetId,
    rollName: `Спасбросок ${STAT_LABELS[stat]}`,
    expression: modExpr(mod),
    fieldValues: { modifier: mod },
    isWhisper: false,
    isDualD20: false,
  };
}

export function rollSkill(
  sheetId: string,
  skill: SkillId,
  data: RulesCharacterData,
): RollRequest {
  const mod = dnd5eRules.activeSkillMod(data, skill);
  return {
    sheetId,
    rollName: SKILL_LABELS[skill],
    expression: modExpr(mod),
    fieldValues: { modifier: mod },
    isWhisper: false,
    isDualD20: false,
  };
}

export function rollAttack(
  sheetId: string,
  attack: RollAttack,
  data: RulesCharacterData,
): RollRequest {
  // `data` is reserved for future contextual modifiers (e.g. proficiency,
  // ability tie-ins). Today the attack bonus is precomputed on the attack
  // entry itself, so we don't read from `data` — but the parameter remains
  // part of the unified call signature for all roll builders.
  void data;
  const mod = attack.attackBonus;
  return {
    sheetId,
    rollName: attack.name,
    expression: modExpr(mod),
    fieldValues: { modifier: mod },
    isWhisper: false,
    isDualD20: false,
    attackDamage: {
      expression: attack.damage,
      type: attack.damageType,
    },
  };
}

export function rollDamage(
  sheetId: string,
  attack: RollAttack,
): RollRequest {
  return {
    sheetId,
    rollName: `${attack.name} (урон)`,
    expression: attack.damage,
    fieldValues: {},
    isWhisper: false,
    isDualD20: false,
  };
}

export function rollSpellAttack(
  sheetId: string,
  data: RulesCharacterData,
): RollRequest {
  const mod = dnd5eRules.spellAttackBonus(data);
  return {
    sheetId,
    rollName: 'Атака заклинанием',
    expression: modExpr(mod),
    fieldValues: { modifier: mod },
    isWhisper: false,
    isDualD20: false,
  };
}

export function rollSpellSave(
  sheetId: string,
  data: RulesCharacterData,
): RollRequest {
  const dc = dnd5eRules.spellDC(data);
  return {
    sheetId,
    rollName: `Spell Save DC ${dc}`,
    expression: `${dc}`,
    fieldValues: { dc },
    isWhisper: false,
    isDualD20: false,
  };
}

export function rollInitiative(
  sheetId: string,
  data: RulesCharacterData,
): RollRequest {
  const mod = dnd5eRules.abilityMod(data.stats.dex);
  return {
    sheetId,
    rollName: 'Инициатива',
    expression: modExpr(mod),
    fieldValues: { modifier: mod },
    isWhisper: false,
    isDualD20: false,
  };
}
